import { ValidationConfig } from 'ihub/common';
import { AdvancedTourStep } from '../shared-types';
import { TaskTourProps } from './task-tour-props';

export function getValidationTaskTourSteps({
	defaultStyles,
	microTaskConfigType,
}: Readonly<TaskTourProps>): AdvancedTourStep[] {
	if (!microTaskConfigType) return [];
	const config = microTaskConfigType as ValidationConfig;

	return [
		{
			id: 'validation-method',
			selector: '[data-tour="validation-method"]',
			content:
				'Defines the validation logic used by the task. Currently the available method is Custom Rules, which allows a static validation model to be configured.',
		},
		{
			id: 'validation-actions',
			selector: '[data-tour="validation-actions"]',
			content:
				'Determines how the validation task behaves when executing rules. "Stop On The First Record With An Error" stops processing once the first error is found and routes the entire batch to the failure destination. "Validate All Records" evaluates every record and routes the batch to failure if any errors are found. This option is commonly used together with "Map Error Message" to allow downstream tasks to separate valid and invalid records.',
		},
		{
			id: 'map-error-message',
			selector: '[data-tour="map-error-message"]',
			content: 'Determines whether the validation error message should be saved to each record.',
		},
		...(config.mapErrorMessage
			? [
					{
						id: 'error-field',
						selector: '[data-tour="error-field"]',
						content:
							'Specifies the field where the validation error message will be stored when "Map Error Message" is enabled. The value is defined as a string path such as "data.error". The "." character represents nested object levels. If the field does not exist it will be created, and if it already exists it will be overwritten.',
					},
				]
			: []),

		{
			id: 'cleanup-properties',
			selector: '[data-tour="cleanup-properties"]',
			content: 'Removes all optional or previously added properties from the properties list.',
		},
		{
			id: 'validation-property',
			selector: '[data-tour="validation-property"]',
			content:
				'Represents a property derived from the input data. Each field in the input payload automatically generates a corresponding property that can be configured with validation rules.',
		},
		{
			id: 'validation-field',
			selector: '[data-tour="validation-field"]',
			content:
				'The field name from the input data associated with this property. Fields are automatically detected from the input payload. If a field is removed from the input data, an option will appear to remove the corresponding property.',
		},
		{
			id: 'fail-if-rule-matches',
			selector: '[data-tour="fail-if-rule-matches"]',
			content:
				'If enabled, the task will fail when the configured rule conditions match for this property.',
		},
		{
			id: 'validation-rule-builder',
			selector: '[data-tour="validation-rule-builder"]',
			content: 'Used to configure the validation logic for the property using the Rule Builder.',
		},
	].map(tourItem => ({ ...tourItem, styles: defaultStyles }));
}
